import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Plus, Lock, Pencil, Trash2, MessageSquarePlus, X, Search, ArrowUp, ArrowDown, ArrowUpDown, ChevronDown, ChevronUp, RefreshCw, Copy, XCircle } from "lucide-react";
import api from "../lib/api";
import { useAuth, useCurrency } from "../lib/contexts";
import { canEdit, canChangeStage, canWrite, timeLeft, fmtDate, STAGES, STAGE_COLORS, stageAgeDays, agingLevel, todayIso, followUpStatus } from "../lib/permissions";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import WonDetailsDialog, { GST_RATES, wonAmounts, dealProducts } from "./deals/WonDetailsDialog";
import ExportMenu from "../components/ExportMenu";
import DuplicatesDialog from "../components/DuplicatesDialog";

const VISIBLE_CARDS = 6;            // cards shown before the "Show N more" toggle
const COLLAPSIBLE_STAGES = ["Won"]; // add "Lost" here if it starts growing too
const AUTO_REFRESH_MS = 15 * 60 * 1000;
import LostReasonDialog from "./deals/LostReasonDialog";
import SampleSentDialog from "./deals/SampleSentDialog";

// Live EXW / FOR / GST readout shared by the deal form and the detail view.
function WonAmountsSummary({ values, title = "Order totals" }) {
  const { format } = useCurrency();
  const { q, p, f: freight, g: gst, exwAmount, freightPerMt, forPricePerMt, forAmount, gstAmount, grandTotal } = wonAmounts(values);
  const row = (label, value, strong, testid) => (
    <div className="flex items-center justify-between">
      <span className={strong ? "font-semibold text-primary" : "text-muted-foreground"}>{label}</span>
      <span className={`tabular-nums ${strong ? "font-bold text-primary" : ""}`} data-testid={testid}>{format(value)}</span>
    </div>
  );
  return (
    <div className="grid grid-cols-2 gap-3" data-testid="won-amounts-summary">
      <div className="rounded-md border border-border bg-white p-3 space-y-1.5 text-sm">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold pb-1 border-b border-border">Per MT rates</div>
        {row("EXW Price (per MT)", p, false, "detail-exw-price")}
        {row("Freight (per MT)", freightPerMt, false, "detail-freight-per-mt")}
        <div className="border-t border-border pt-1.5">{row("Total FOR Price (per MT)", forPricePerMt, true, "detail-for-price-per-mt")}</div>
      </div>
      <div className="rounded-md border border-border bg-secondary/40 p-3 space-y-1.5 text-sm">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold pb-1 border-b border-border">
          {title}{q > 0 && <span className="font-normal normal-case tracking-normal"> · {q} MT</span>}
        </div>
        {row("Total EXW Value", exwAmount, false, "detail-exw-amount")}
        {row("Total Freight", freight, false, "detail-freight-amount")}
        <div className="border-t border-border pt-1.5">{row("Total FOR Value (EXW + Freight)", forAmount, false, "detail-for-amount")}</div>
        {row(`GST @ ${gst}%`, gstAmount, false, "detail-gst-amount")}
        <div className="border-t border-border pt-1.5">{row("Grand Total", grandTotal, true, "detail-grand-total")}</div>
      </div>
      <div className="col-span-2 text-[10px] text-muted-foreground -mt-1">Booked value for reporting = Total EXW Value.</div>
    </div>
  );
}

const emptyProductRow = () => ({ _key: `p-${Math.random().toString(36).slice(2)}`, product_name: "", product_code: "", status: "open" });

function DealForm({ open, onClose, initial, contacts, users, fields, products, currentUser, onSave }) {
  const [f, setF] = useState({
    contact_id: "", products: [emptyProductRow()], stage: "New Lead", estimated_value: "",
    currency: "INR", follow_up_date: "", po_number: "", pi_number: "",
    quantity_mt: "", exw_price: "", freight_amount: "", gst_percent: 18, custom_fields: {}
  });
  const [showAllProducts, setShowAllProducts] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (initial) {
      const rows = (Array.isArray(initial.products) && initial.products.length
        ? initial.products
        : [{ product_name: initial.product_name || "", product_code: initial.product_code || "", status: "open" }]
      ).map((p, i) => ({ ...p, _key: p.id || `p-${i}` }));
      setF((prev) => ({
        ...prev, ...initial, products: rows,
        estimated_value: initial.estimated_value ?? "",
        quantity_mt: initial.quantity_mt ?? "",
        exw_price: initial.exw_price ?? "",
        freight_amount: initial.freight_amount ?? "",
        gst_percent: initial.gst_percent ?? 18,
        follow_up_date: initial.follow_up_date ? initial.follow_up_date.slice(0, 10) : "",
      }));
    } else setF({
      contact_id: "", products: [emptyProductRow()], stage: "New Lead", estimated_value: "",
      currency: "INR", follow_up_date: "", po_number: "", pi_number: "",
      quantity_mt: "", exw_price: "", freight_amount: "", gst_percent: 18, custom_fields: {}
    });
    // eslint-disable-next-line
  }, [initial, open]);

  const selectedContact = contacts.find((c) => c.id === f.contact_id);
  const contactProducts = (() => {
    if (!selectedContact) return null;
    const arr = Array.isArray(selectedContact.products) && selectedContact.products.length
      ? selectedContact.products
      : (selectedContact.product_name ? [{ product_name: selectedContact.product_name, product_code: selectedContact.product_code }] : []);
    return arr.filter((p) => (p.product_name || "").trim());
  })();
  const isManager = currentUser?.role === "manager";
  const restrictToContact = selectedContact && contactProducts && contactProducts.length > 0 && !showAllProducts;
  const productNameOptions = restrictToContact
    ? Array.from(new Set(contactProducts.map((p) => p.product_name).filter(Boolean)))
    : products.map((p) => p.name);
  const codeOptions = (name) => restrictToContact
    ? Array.from(new Set(contactProducts.filter((p) => p.product_name === name).map((p) => p.product_code).filter(Boolean)))
    : ((products.find((p) => p.name === name)?.codes) || []).map((c) => c.code);

  const setRow = (idx, patch) => setF((s) => ({
    ...s, products: s.products.map((r, i) => (i === idx ? { ...r, ...patch } : r)),
  }));
  const onRowProduct = (idx, name) => {
    const stillValid = codeOptions(name).includes(f.products[idx]?.product_code);
    setRow(idx, { product_name: name, product_code: stillValid ? f.products[idx].product_code : "" });
  };
  const addRow = () => setF((s) => ({ ...s, products: [...s.products, emptyProductRow()] }));
  const removeRow = (idx) => setF((s) => ({
    ...s, products: s.products.length > 1 ? s.products.filter((_, i) => i !== idx) : s.products,
  }));
  const multi = f.products.length > 1;
  // A multi-product deal captures its Won figures in the Won dialog, not here —
  // but an already-Won deal must still show its real stage.
  const stageOptions = multi
    ? STAGES.filter((s) => s !== "Won" || initial?.stage === "Won" || f.stage === "Won")
    : STAGES;

  const submittingRef = React.useRef(false);

  const submit = async (e) => {
    e.preventDefault();
    if (submittingRef.current) return; // same-tick double click
    // Client-side required-field validation
    const missing = [];
    if (!f.contact_id) missing.push("Company Name");
    f.products.forEach((r, i) => {
      if (!r.product_name?.trim()) missing.push(`Product #${i + 1} — Product Name`);
      if (!r.product_code?.trim()) missing.push(`Product #${i + 1} — Product Code`);
    });
    const seen = new Set();
    const dupes = [];
    f.products.forEach((r) => {
      const key = `${(r.product_name || "").toLowerCase()}|${(r.product_code || "").toLowerCase()}`;
      if (seen.has(key) && r.product_name) dupes.push(r.product_name);
      seen.add(key);
    });
    if (!f.stage) missing.push("Stage");
    if (!f.currency) missing.push("Currency");
    if (!f.estimated_value || Number(f.estimated_value) <= 0) missing.push("Estimated Deal Value");
    if (!f.follow_up_date) missing.push("Follow-up Date");
    else if (f.follow_up_date < todayIso()) missing.push("Follow-up Date (cannot be in the past)");
    if (f.stage === "Won" && !multi) {
      if (!f.pi_number?.trim()) missing.push("Our PI Number");
      if (!f.quantity_mt || Number(f.quantity_mt) <= 0) missing.push("Quantity (MT)");
      if (!f.exw_price || Number(f.exw_price) <= 0) missing.push("EXW Price");
    }
    if (missing.length) {
      toast.error(`Please fill in the required field(s): ${missing.join(", ")}`);
      return;
    }
    if (dupes.length) {
      toast.error(`${dupes.join(", ")} is already on this deal`, {
        description: "Pick a different product or code for that row.",
      });
      return;
    }
    const rows = f.products.map((r, i) => {
      // On a single-product deal the Won fields live on the form itself, so the
      // edited form values must win over whatever is stored on the row.
      const useForm = !multi && i === 0;
      const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
      return {
        id: r.id, product_name: r.product_name.trim(), product_code: (r.product_code || "").trim(),
        status: f.stage === "Won" && !multi && i === 0 ? "won" : (r.status === "not_taken" ? "not_taken" : r.status === "won" ? "won" : "open"),
        lost_reason: r.lost_reason || "",
        po_number: useForm ? (f.po_number || "") : (r.po_number || ""),
        pi_number: useForm ? (f.pi_number || "") : (r.pi_number || ""),
        quantity_mt: useForm ? num(f.quantity_mt) : (r.quantity_mt ?? null),
        exw_price: useForm ? num(f.exw_price) : (r.exw_price ?? null),
        freight_amount: useForm ? (Number(f.freight_amount) || 0) : (r.freight_amount ?? null),
        gst_percent: useForm ? (Number(f.gst_percent) || 0) : (r.gst_percent ?? null),
      };
    });
    const payload = { ...f, products: rows,
      product_name: rows[0].product_name, product_code: rows[0].product_code,
      estimated_value: Number(f.estimated_value) || 0,
      quantity_mt: f.quantity_mt === "" ? null : Number(f.quantity_mt),
      exw_price: f.exw_price === "" ? null : Number(f.exw_price),
      freight_amount: Number(f.freight_amount) || 0,
      gst_percent: Number(f.gst_percent) || 0,
      follow_up_date: f.follow_up_date ? new Date(f.follow_up_date).toISOString() : null,
    };
    try {
      setSaving(true);
      submittingRef.current = true;
      const res = initial ? await api.put(`/deals/${initial.id}`, payload) : await api.post("/deals", payload);
      toast.success(initial ? "Deal updated" : res.data?.duplicate_of ? "Deal already created" : "Deal created");
      onSave(res.data); onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    submittingRef.current = false;
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">{initial ? "Edit Deal" : "New Deal"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-2 gap-4">
          <div className="col-span-2"><Label>Company Name <span className="text-destructive">*</span></Label>
            <select data-testid="deal-contact" value={f.contact_id} onChange={(e) => setF({ ...f, contact_id: e.target.value })}
              className="w-full h-10 rounded-md border border-input bg-white px-3 text-sm">
              <option value="">— Select company —</option>
              {contacts.map((c) => <option key={c.id} value={c.id}>{c.company_name}</option>)}
            </select>
          </div>
          <div className="col-span-2 rounded-md border border-border bg-secondary/30 p-3 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <Label className="text-sm">Products <span className="text-destructive">*</span></Label>
              <span className="text-[11px] text-muted-foreground">{f.products.length} product{f.products.length === 1 ? "" : "s"}</span>
            </div>
            {f.products.map((row, idx) => (
              <div key={row._key} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-start">
                <select data-testid={`deal-product-${idx}`} value={row.product_name}
                  onChange={(e) => onRowProduct(idx, e.target.value)}
                  className="h-10 rounded-md border border-input bg-white px-3 text-sm">
                  <option value="">— Select a product —</option>
                  {productNameOptions.map((name) => <option key={name} value={name}>{name}</option>)}
                  {row.product_name && !productNameOptions.includes(row.product_name) && (
                    <option value={row.product_name}>{row.product_name} (legacy)</option>
                  )}
                </select>
                <select data-testid={`deal-product-code-${idx}`} value={row.product_code || ""}
                  onChange={(e) => setRow(idx, { product_code: e.target.value })}
                  disabled={!row.product_name}
                  className="h-10 rounded-md border border-input bg-white px-3 text-sm disabled:bg-neutral-100 disabled:cursor-not-allowed">
                  <option value="">
                    {!row.product_name ? "— Select a product first —" : codeOptions(row.product_name).length === 0 ? "— No codes available —" : "— Select a code —"}
                  </option>
                  {codeOptions(row.product_name).map((c) => <option key={c} value={c}>{c}</option>)}
                  {row.product_code && !codeOptions(row.product_name).includes(row.product_code) && (
                    <option value={row.product_code}>{row.product_code} (legacy)</option>
                  )}
                </select>
                <Button type="button" variant="ghost" size="sm" data-testid={`remove-product-${idx}`}
                  disabled={f.products.length === 1} onClick={() => removeRow(idx)}
                  className="h-10 px-2 text-muted-foreground hover:text-destructive disabled:opacity-30">
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ))}
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <Button type="button" variant="outline" size="sm" data-testid="add-product-row" onClick={addRow}
                disabled={!f.contact_id}>
                <Plus className="w-3 h-3 mr-1" />Add another product
              </Button>
              {selectedContact && contactProducts && contactProducts.length > 0 && (
                <div className="text-[11px] text-muted-foreground flex items-center gap-2">
                  {restrictToContact
                    ? <>Only products this customer buys ({contactProducts.length}). Missing one? Add it to the contact first.</>
                    : <>Showing all products in the master.</>}
                  {isManager && (
                    <button type="button" onClick={() => setShowAllProducts(!showAllProducts)}
                      data-testid="show-all-products-toggle"
                      className="text-primary hover:underline">
                      {showAllProducts ? "Restrict to customer" : "Show all"}
                    </button>
                  )}
                </div>
              )}
            </div>
            {multi && (
              <p className="text-[11px] text-muted-foreground border-t border-border pt-2" data-testid="multi-product-hint">
                Invoice figures are captured per product when the deal moves to Won.
              </p>
            )}
          </div>
          <div><Label>Stage <span className="text-destructive">*</span></Label>
            <select data-testid="deal-stage" value={f.stage} onChange={(e) => setF({ ...f, stage: e.target.value })}
              className="w-full h-10 rounded-md border border-input bg-white px-3 text-sm">
              {stageOptions.map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div><Label>Currency <span className="text-destructive">*</span></Label>
            <select value={f.currency} onChange={(e) => setF({ ...f, currency: e.target.value })}
              className="w-full h-10 rounded-md border border-input bg-white px-3 text-sm">
              <option>INR</option><option>USD</option>
            </select>
          </div>
          <div><Label>Estimated Deal Value <span className="text-destructive">*</span></Label>
            <Input type="number" step="0.01" data-testid="deal-est" value={f.estimated_value}
              onChange={(e) => setF({ ...f, estimated_value: e.target.value })} />
          </div>
          <div><Label>Follow-up Date <span className="text-destructive">*</span></Label>
            <Input type="date" data-testid="deal-follow-up" value={f.follow_up_date}
              min={todayIso()}
              onChange={(e) => setF({ ...f, follow_up_date: e.target.value })} />
          </div>
          {f.stage === "Won" && !multi && (
            <>
              <div className="col-span-2 mt-2 pt-3 border-t border-border">
                <div className="text-xs uppercase tracking-widest text-primary font-semibold">Won Deal — Proforma Invoice Details</div>
              </div>
              <div><Label>Our PI Number <span className="text-destructive">*</span></Label>
                <Input data-testid="deal-pi" value={f.pi_number} onChange={(e) => setF({ ...f, pi_number: e.target.value })} />
              </div>
              <div><Label>Customer PO Number</Label>
                <Input data-testid="deal-po" value={f.po_number} onChange={(e) => setF({ ...f, po_number: e.target.value })} placeholder="Optional" />
              </div>
              <div><Label>Quantity (MT) <span className="text-destructive">*</span></Label>
                <Input type="number" step="0.001" data-testid="deal-quantity" value={f.quantity_mt}
                  onChange={(e) => setF({ ...f, quantity_mt: e.target.value })} />
              </div>
              <div><Label>EXW Price (per MT) <span className="text-destructive">*</span></Label>
                <Input type="number" step="0.01" data-testid="deal-exw-price" value={f.exw_price}
                  onChange={(e) => setF({ ...f, exw_price: e.target.value })} />
              </div>
              <div><Label>Freight (if any, e.g. 20,000)</Label>
                <Input type="number" step="0.01" data-testid="deal-freight" value={f.freight_amount}
                  placeholder="0" onChange={(e) => setF({ ...f, freight_amount: e.target.value })} />
              </div>
              <div><Label>GST (%)</Label>
                <select data-testid="deal-gst" value={f.gst_percent}
                  onChange={(e) => setF({ ...f, gst_percent: e.target.value })}
                  className="w-full h-10 rounded-md border border-input bg-white px-3 text-sm">
                  {GST_RATES.map((r) => <option key={r} value={r}>{r}%</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <WonAmountsSummary values={{
                  quantity_mt: f.quantity_mt, exw_price: f.exw_price,
                  freight_amount: f.freight_amount, gst_percent: f.gst_percent,
                }} />
              </div>
            </>
          )}
          {fields.map((cf) => (
            <div key={cf.id}><Label>{cf.label}</Label>
              <Input type={cf.type === "number" ? "number" : cf.type === "date" ? "date" : "text"}
                value={f.custom_fields[cf.name] || ""}
                onChange={(e) => setF({ ...f, custom_fields: { ...f.custom_fields, [cf.name]: e.target.value } })} />
            </div>
          ))}
          <div className="col-span-2 flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" data-testid="deal-save" disabled={saving}
              className="bg-primary hover:bg-primary/90 text-white disabled:opacity-60">
              {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DealDetail({ deal, onClose, onRefresh, user, onChangeStage, onEditLostReason, onEditWonDetails }) {
  const [note, setNote] = useState("");
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const { format } = useCurrency();
  useEffect(() => {
    if (!deal) { setHistory([]); return; }
    api.get(`/audit-log/deal/${deal.id}`).then((r) => setHistory(r.data)).catch(() => {});
  }, [deal]);
  if (!deal) return null;
  const canMove = canChangeStage(user, deal);
  const addNote = async (e) => {
    e.preventDefault();
    if (!note.trim()) return;
    try { await api.post(`/deals/${deal.id}/notes`, { text: note }); setNote(""); onRefresh(); toast.success("Note added"); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open={!!deal} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">
            <div className="flex flex-wrap items-center gap-2">
              <span>{deal.product_name || "—"}</span>
              {deal.product_code && <span className="text-sm font-mono font-normal bg-neutral-100 px-2 py-0.5 rounded">{deal.product_code}</span>}
              {dealProducts(deal).length > 1 && (
                <span className="text-xs font-normal bg-primary/10 text-primary px-2 py-0.5 rounded" data-testid="detail-more-products">
                  +{dealProducts(deal).length - 1} more
                </span>
              )}
              <span className={`text-xs px-2 py-1 rounded border ${STAGE_COLORS[deal.stage]}`}>{deal.stage}</span>
              {canMove && (
                <select data-testid="detail-change-stage" value={deal.stage}
                  onChange={(e) => onChangeStage && onChangeStage(deal, e.target.value)}
                  className="text-xs h-7 rounded border border-input bg-white px-2 font-normal"
                  title="Change stage">
                  {STAGES.map((s) => <option key={s} value={s}>Move to: {s}</option>)}
                </select>
              )}
            </div>
            <div className="text-sm font-normal text-muted-foreground mt-1">{deal.contact_name}</div>
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div><div className="text-xs uppercase tracking-widest text-muted-foreground">Owner</div><div>{deal.created_by_name}</div></div>
          <div><div className="text-xs uppercase tracking-widest text-muted-foreground">Currency</div><div>{deal.currency || "INR"}</div></div>
          <div><div className="text-xs uppercase tracking-widest text-muted-foreground">Estimated</div><div className="font-semibold">{format(deal.estimated_value)}</div></div>
          <div><div className="text-xs uppercase tracking-widest text-muted-foreground">Booked (EXW)</div><div className="font-semibold text-primary">{deal.actual_value != null ? format(deal.actual_value) : "—"}</div></div>
          {deal.stage === "Won" && <>
            <div><div className="text-xs uppercase tracking-widest text-muted-foreground">Closed</div><div>{fmtDate(deal.closed_at)}</div></div>
            <div><div className="text-xs uppercase tracking-widest text-muted-foreground">Products won</div>
              <div>{dealProducts(deal).filter((p) => p.status === "won").length} of {dealProducts(deal).length}</div>
            </div>
            <div className="col-span-2 space-y-3" data-testid="won-products">
              {dealProducts(deal).map((p, i) => (
                <div key={p.id || `wp-${i}`} data-testid={`won-product-detail-${i}`}
                  className={`rounded-md border p-3 space-y-2 ${p.status === "won" ? "border-emerald-200 bg-emerald-50/30" : "border-border bg-secondary/40"}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className={`font-semibold ${p.status === "won" ? "" : "text-muted-foreground line-through"}`}>{p.product_name || "—"}</span>
                      {p.product_code && <span className="text-[10px] font-mono bg-neutral-200 px-1.5 py-0.5 rounded">{p.product_code}</span>}
                    </div>
                    <span className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded border ${
                      p.status === "won" ? "border-emerald-300 text-emerald-800 bg-emerald-100" : "border-border text-muted-foreground"}`}>
                      {p.status === "won" ? "Won" : "Not taken"}
                    </span>
                  </div>
                  {p.status === "won" ? (
                    <>
                      <div className="grid grid-cols-4 gap-2 text-xs">
                        <div><div className="text-muted-foreground">PI</div><div className="font-medium">{p.pi_number || "—"}</div></div>
                        <div><div className="text-muted-foreground">PO</div><div className="font-medium">{p.po_number || "—"}</div></div>
                        <div><div className="text-muted-foreground">Quantity</div><div className="font-medium">{p.quantity_mt ? `${p.quantity_mt} MT` : "—"}</div></div>
                        <div><div className="text-muted-foreground">EXW / MT</div><div className="font-medium">{p.exw_price ? format(p.exw_price) : "—"}</div></div>
                      </div>
                      {p.quantity_mt && p.exw_price ? (
                        <WonAmountsSummary title="Product totals" values={{
                          quantity_mt: p.quantity_mt, exw_price: p.exw_price,
                          freight_amount: p.freight_amount, gst_percent: p.gst_percent,
                        }} />
                      ) : (
                        <div className="rounded-md border border-dashed border-border bg-secondary/30 p-2 text-xs text-muted-foreground" data-testid="won-breakdown-missing">
                          Invoice breakdown not captured for this product — booked value is {deal.actual_value != null ? format(deal.actual_value) : "—"}.
                          {user?.role === "manager" && " Use the button below to add Quantity, EXW price, Freight and GST."}
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="text-xs text-muted-foreground" data-testid={`won-product-lost-reason-${i}`}>
                      <span className="uppercase tracking-widest text-[10px] text-destructive font-semibold">Reason for loss</span>
                      <br />
                      {p.lost_reason || "No reason recorded (product marked Not taken before reasons were required)."}
                    </p>
                  )}
                </div>
              ))}
            </div>
            {user?.role === "manager" && onEditWonDetails && (
              <div className="col-span-2">
                <Button type="button" variant="outline" size="sm" data-testid="edit-won-details"
                  onClick={() => onEditWonDetails(deal)}
                  className="h-7 px-2 text-[11px] border-emerald-300 text-emerald-700 hover:bg-emerald-50">
                  <Pencil className="w-3 h-3 mr-1" />Edit PI details (Qty / EXW / Freight / GST)
                </Button>
                <p className="text-[10px] text-muted-foreground mt-1">Manager-only correction. Changes are logged in the change history.</p>
              </div>
            )}
          </>}
          <div><div className="text-xs uppercase tracking-widest text-muted-foreground">Created</div><div>{fmtDate(deal.created_at)}</div></div>
          <div><div className="text-xs uppercase tracking-widest text-muted-foreground">Follow-up</div>
            <div>{(() => {
              const fu = followUpStatus(deal);
              const cls = fu === "overdue" ? "text-destructive font-semibold" : fu === "today" ? "text-amber-700 font-semibold" : "";
              return <span className={cls}>{fmtDate(deal.follow_up_date)}{fu === "overdue" ? " · overdue" : fu === "today" ? " · today" : ""}</span>;
            })()}</div>
          </div>
          {deal.sample_sent_date && (
            <div><div className="text-xs uppercase tracking-widest text-muted-foreground">Sample Sent</div><div>{fmtDate(deal.sample_sent_date)}</div></div>
          )}
        </div>
        {deal.stage !== "Won" && dealProducts(deal).length > 1 && (
          <div className="mt-4 rounded-md border border-border bg-secondary/30 p-3" data-testid="deal-products-list">
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Products on this enquiry</div>
            <div className="flex flex-wrap gap-2">
              {dealProducts(deal).map((p, i) => (
                <span key={p.id || `dp-${i}`} className="inline-flex items-center gap-1.5 text-sm bg-white border border-border rounded px-2 py-1">
                  {p.product_name}
                  {p.product_code && <span className="text-[10px] font-mono bg-neutral-100 px-1 rounded">{p.product_code}</span>}
                </span>
              ))}
            </div>
          </div>
        )}
        {deal.stage === "Lost" && (
          <div className="mt-4 border-l-4 border-destructive bg-destructive/5 rounded-r-md p-3" data-testid="deal-lost-reason">
            <div className="flex items-start justify-between gap-2">
              <div className="text-xs uppercase tracking-widest text-destructive font-semibold mb-1">Lost Reason</div>
              {canMove && onEditLostReason && (
                <Button type="button" variant="outline" size="sm" data-testid="edit-lost-reason"
                  onClick={() => onEditLostReason(deal)}
                  className="h-6 px-2 text-[11px] border-destructive/40 text-destructive hover:bg-destructive/10">
                  <Pencil className="w-3 h-3 mr-1" />Edit reason
                </Button>
              )}
            </div>
            <div className="text-sm text-neutral-800 whitespace-pre-wrap">{deal.lost_reason || "—"}</div>
            <div className="text-[10px] text-muted-foreground mt-1">Closed: {fmtDate(deal.closed_at)}</div>
          </div>
        )}
        <div className="mt-6">
          <h4 className="font-heading font-semibold mb-2">Activity & Notes</h4>
          <form onSubmit={addNote} className="flex gap-2 mb-3">
            <Input data-testid="deal-note-input" placeholder="Add a note or activity log…" value={note} onChange={(e) => setNote(e.target.value)} />
            <Button data-testid="deal-note-submit" type="submit" size="sm" className="bg-primary hover:bg-primary/90 text-white"><MessageSquarePlus className="w-4 h-4" /></Button>
          </form>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {(deal.notes || []).slice().reverse().map((n) => (
              <div key={n.id} className="bg-secondary/50 border border-border rounded-md p-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-xs">{n.author_name}</span>
                  <span className="text-[10px] text-muted-foreground">{new Date(n.created_at).toLocaleString()}</span>
                </div>
                <div className="mt-1 text-neutral-800">{n.text}</div>
              </div>
            ))}
            {(!deal.notes || deal.notes.length === 0) && <div className="text-xs text-muted-foreground">No notes yet.</div>}
          </div>
        </div>
        <div className="mt-6 border-t border-border pt-4">
          <button type="button" onClick={() => setShowHistory(!showHistory)}
            data-testid="toggle-history"
            className="flex items-center gap-2 text-sm font-heading font-semibold text-neutral-800 hover:text-primary">
            <span>{showHistory ? "▼" : "▶"}</span>
            <span>Change History</span>
            <span className="text-xs font-normal text-muted-foreground">({history.length})</span>
          </button>
          {showHistory && (
            <div className="mt-3 space-y-2 max-h-60 overflow-y-auto" data-testid="history-list">
              {history.length === 0 && <div className="text-xs text-muted-foreground italic">No changes recorded.</div>}
              {history.map((h) => (
                <div key={h.id} className="bg-secondary/30 border border-border rounded-md p-2 text-xs">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-neutral-800">{h.user_name}</span>
                    <span className="text-[10px] text-muted-foreground">{new Date(h.created_at).toLocaleString()}</span>
                  </div>
                  <div className="text-neutral-700 mt-0.5">{h.summary || h.action}</div>
                  {h.changes?.length > 0 && (
                    <ul className="mt-1 space-y-0.5 text-[11px] text-muted-foreground pl-3">
                      {h.changes.slice(0, 5).map((c) => (
                        <li key={`${h.id}-${c.field}`}><code>{c.field}</code>: <span className="line-through">{JSON.stringify(c.before)?.slice(0, 40)}</span> → {JSON.stringify(c.after)?.slice(0, 40)}</li>
                      ))}
                      {h.changes.length > 5 && <li>+{h.changes.length - 5} more field(s)</li>}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}


// Clickable table header: first click sorts A→Z, second Z→A, third clears.
function SortableTh({ label, field, sort, onSort }) {
  const active = sort.field === field;
  const Icon = !active ? ArrowUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <th className="text-left px-4 py-3">
      <button type="button" data-testid={`sort-${field}`} onClick={() => onSort(field)}
        title={`Sort by ${label}`}
        className={`flex items-center gap-1 uppercase tracking-widest transition-colors hover:text-primary ${active ? "text-primary font-semibold" : ""}`}>
        {label}
        <Icon className={`w-3 h-3 ${active ? "opacity-100" : "opacity-40"}`} />
      </button>
    </th>
  );
}

export default function Deals() {
  const { user } = useAuth();
  const { format } = useCurrency();
  const [deals, setDeals] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [users, setUsers] = useState([]);
  const [fields, setFields] = useState([]);
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [stageFilter, setStageFilter] = useState("");
  const [staffFilter, setStaffFilter] = useState("");
  const [dialog, setDialog] = useState({ open: false, initial: null });
  const [detailDeal, setDetailDeal] = useState(null);
  const [view, setView] = useState("kanban");
  const [dragging, setDragging] = useState(null);
  const [wonPrompt, setWonPrompt] = useState(null); // { deal } — dialog for capturing PO/PI/Actual on drag→Won
  const [lostPrompt, setLostPrompt] = useState(null); // { deal } — dialog for capturing Lost reason on drag→Lost
  const [samplePrompt, setSamplePrompt] = useState(null); // { deal } — dialog for capturing Sample Sent Date + Next Reminder on drag→Sample Sent
  const [sort, setSort] = useState({ field: "", dir: "asc" }); // table view column sort
  // Won only ever grows, so it opens collapsed to its newest cards.
  const [expandedColumns, setExpandedColumns] = useState({});
  const [lastSync, setLastSync] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [dupOpen, setDupOpen] = useState(false);
  const [dupCount, setDupCount] = useState(0);

  const toggleSort = (field) => setSort((s) =>
    s.field !== field ? { field, dir: "asc" }
      : s.dir === "asc" ? { field, dir: "desc" }
      : { field: "", dir: "asc" }
  );

  // Stage sorts by pipeline order (New Lead → Lost), everything else A→Z.
  const sortedDeals = useMemo(() => {
    if (!sort.field) return deals;
    const val = (d) => sort.field === "stage"
      ? STAGES.indexOf(d.stage)
      : (d[sort.field] || "").toString().toLowerCase();
    const factor = sort.dir === "asc" ? 1 : -1;
    return [...deals].sort((a, b) => {
      const va = val(a), vb = val(b);
      if (va < vb) return -1 * factor;
      if (va > vb) return 1 * factor;
      return 0;
    });
  }, [deals, sort]);

  const load = useCallback(async () => {
    const params = {};
    if (search) params.search = search;
    if (stageFilter) params.stage = stageFilter;
    if (staffFilter) params.created_by = staffFilter;
    const [d, ff] = await Promise.all([
      api.get("/deals", { params }),
      api.get("/fields", { params: { entity: "deal" } }),
    ]);
    setDeals(d.data); setFields(ff.data);
    setLastSync(new Date());
    if (user?.role === "manager") {
      api.get("/deals/duplicates")
        .then((r) => setDupCount((r.data || []).reduce((a, g) => a + (g.count - 1), 0)))
        .catch(() => {});
    }
  }, [search, stageFilter, staffFilter, user?.role]);
  // Reference data (contacts, users, products) changes rarely — fetch it once.
  useEffect(() => {
    Promise.all([api.get("/contacts"), api.get("/users"), api.get("/products")])
      .then(([c, u, p]) => { setContacts(c.data); setUsers(u.data); setProducts(p.data); })
      .catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  // Board refreshes itself every 15 minutes (and whenever the tab regains focus)
  // so staff never work off a stale pipeline. Uses a ref so the timer is set once.
  const loadRef = useRef(load);
  loadRef.current = load;
  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState !== "visible") return;
      setSyncing(true);
      loadRef.current().catch(() => {}).finally(() => setSyncing(false));
    };
    const id = setInterval(refresh, AUTO_REFRESH_MS);
    document.addEventListener("visibilitychange", refresh);
    return () => { clearInterval(id); document.removeEventListener("visibilitychange", refresh); };
  }, []);

  const grouped = useMemo(() => {
    const g = Object.fromEntries(STAGES.map((s) => [s, []]));
    const seen = new Set();
    deals.forEach((d) => {
      if (seen.has(d.id)) return; // never render the same deal twice
      seen.add(d.id);
      if (g[d.stage]) g[d.stage].push(d);
    });
    return g;
  }, [deals]);

  const stageTotals = useMemo(() => Object.fromEntries(STAGES.map((s) =>
    [s, grouped[s].reduce((a, d) => a + (d.stage === "Won" ? (d.actual_value || 0) : (d.estimated_value || 0)), 0)]
  )), [grouped]);

  const toggleColumn = (stage) => setExpandedColumns((e) => ({ ...e, [stage]: !e[stage] }));

  // Newest closure first; falls back to created_at for records with no closed date.
  const wonSorted = useMemo(() => [...(grouped.Won || [])].sort((a, b) =>
    String(b.closed_at || b.created_at || "").localeCompare(String(a.closed_at || a.created_at || ""))
  ), [grouped]);

  const columnCards = (stage) => (stage === "Won" ? wonSorted : grouped[stage]);
  const visibleCards = (stage) => {
    const all = columnCards(stage);
    if (!COLLAPSIBLE_STAGES.includes(stage) || expandedColumns[stage]) return all;
    return all.slice(0, VISIBLE_CARDS);
  };
  const hiddenCount = (stage) =>
    COLLAPSIBLE_STAGES.includes(stage) ? Math.max(columnCards(stage).length - VISIBLE_CARDS, 0) : 0;


  const onDrop = async (stage) => {
    if (!dragging) return;
    if (dragging.stage === stage) { setDragging(null); return; }
    if (!canChangeStage(user, dragging)) { toast.error("You can only move your own deals"); setDragging(null); return; }
    // Won requires PO / PI / Actual; Lost requires a reason; Sample Sent needs date + reminder — capture via a small prompt before patching.
    if (stage === "Won")         { setWonPrompt({ deal: dragging });    setDragging(null); return; }
    if (stage === "Lost")        { setLostPrompt({ deal: dragging });   setDragging(null); return; }
    if (stage === "Sample Sent") { setSamplePrompt({ deal: dragging }); setDragging(null); return; }
    try {
      await api.patch(`/deals/${dragging.id}/stage`, { stage });
      toast.success(`Moved to ${stage}`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Move failed"); }
    setDragging(null);
  };

  const changeStage = async (deal, stage) => {
    if (!deal || deal.stage === stage) return;
    if (!canChangeStage(user, deal)) { toast.error("You can only move your own deals"); return; }
    if (stage === "Won")         { setWonPrompt({ deal });    return; }
    if (stage === "Lost")        { setLostPrompt({ deal });   return; }
    if (stage === "Sample Sent") { setSamplePrompt({ deal }); return; }
    try {
      await api.patch(`/deals/${deal.id}/stage`, { stage });
      toast.success(`Moved to ${stage}`);
      load();
      if (detailDeal?.id === deal.id) {
        const r = await api.get(`/deals/${deal.id}`); setDetailDeal(r.data);
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Move failed"); }
  };

  const remove = async (d) => {
    if (!confirm(`Delete deal "${d.title}"?`)) return;
    try { await api.delete(`/deals/${d.id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold">Deals Pipeline</h1>
          <p className="text-sm text-muted-foreground">{deals.length} opportunities</p>
        </div>
        <div className="flex gap-2 items-center">
          <button type="button" data-testid="refresh-deals" onClick={() => {
              setSyncing(true);
              load().catch(() => {}).finally(() => setSyncing(false));
            }}
            title="Refreshes automatically every 15 minutes — click to refresh now"
            className="flex items-center gap-1.5 text-[11px] text-muted-foreground hover:text-primary transition-colors mr-1">
            <RefreshCw className={`w-3.5 h-3.5 ${syncing ? "animate-spin" : ""}`} />
            <span data-testid="last-sync">
              {syncing ? "Refreshing…" : lastSync ? `Updated ${lastSync.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : "—"}
            </span>
          </button>
          <div className="flex bg-neutral-100 rounded-md p-0.5">
            {["kanban", "table"].map((v) => (
              <button key={v} data-testid={`view-${v}`} onClick={() => { setView(v); setExpandedColumns({}); }}
                className={`px-3 py-1 text-xs font-semibold rounded ${view === v ? "bg-white text-primary shadow-sm" : "text-neutral-500"}`}>
                {v === "kanban" ? "Kanban" : "Table"}
              </button>
            ))}
          </div>
          {user?.role === "manager" && (
            <Button variant="outline" data-testid="open-duplicates" onClick={() => setDupOpen(true)}
              className={dupCount > 0 ? "border-amber-400 text-amber-700 hover:bg-amber-50" : ""}>
              <Copy className="w-4 h-4 mr-2" />
              {dupCount > 0 ? `${dupCount} duplicate${dupCount === 1 ? "" : "s"}` : "Duplicates"}
            </Button>
          )}
          {(user?.role === "manager" || user?.role === "unit_head") && (
            <ExportMenu resource="deals" params={{ search, stage: stageFilter, created_by: staffFilter }} />
          )}
          {canWrite(user) && (
            <Button data-testid="new-deal-btn" className="bg-primary hover:bg-primary/90 text-white" onClick={() => setDialog({ open: true, initial: null })}>
              <Plus className="w-4 h-4 mr-2" />New Deal
            </Button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-md border border-border p-3 flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="deal-search" placeholder="Search product, code or company…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
        </div>
        <select data-testid="deal-stage-filter" value={stageFilter} onChange={(e) => setStageFilter(e.target.value)} className="h-10 rounded-md border border-input bg-white px-3 text-sm">
          <option value="">All stages</option>
          {STAGES.map((s) => <option key={s}>{s}</option>)}
        </select>
        <select data-testid="deal-staff-filter" value={staffFilter} onChange={(e) => setStaffFilter(e.target.value)} className="h-10 rounded-md border border-input bg-white px-3 text-sm">
          <option value="">All staff</option>
          {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
        {(search || stageFilter || staffFilter) && (
          <button onClick={() => { setSearch(""); setStageFilter(""); setStaffFilter(""); }} className="text-xs text-muted-foreground flex items-center gap-1"><X className="w-3 h-3" />Clear</button>
        )}
      </div>

      {view === "kanban" ? (
        <div className="flex gap-4 overflow-x-auto pb-4 kanban-scroll" data-testid="kanban-board">
          {STAGES.map((s) => (
            <div key={s} className="min-w-[280px] w-[280px]"
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDrop(s)}>
              <div className={`px-3 py-2 rounded-md border-2 border-dashed border-transparent ${STAGE_COLORS[s]} mb-2`}>
                <div className="flex items-center justify-between">
                  <span className="font-heading font-semibold text-sm">{s}</span>
                  <span className="text-xs font-semibold">{grouped[s].length}</span>
                </div>
                <div className="text-[11px] mt-0.5 opacity-80">{format(stageTotals[s])}</div>
              </div>
              <div className="space-y-2 min-h-[100px]">
                {visibleCards(s).map((d, idx) => {
                  const editable = canEdit(user, d);
                  const stageMovable = canChangeStage(user, d);
                  return (
                    <div key={d.id} data-testid={`kanban-card-${d.id}`}
                      draggable={stageMovable}
                      onDragStart={() => setDragging(d)}
                      onDragEnd={() => setDragging(null)}
                      onClick={() => setDetailDeal(d)}
                      title={stageMovable && !editable ? "Edit window expired — you can still drag this deal between stages" : undefined}
                      className={`kanban-card bg-white border border-border rounded-md p-3 cursor-pointer ${dragging?.id === d.id ? "dragging" : ""} ${expandedColumns[s] && idx >= VISIBLE_CARDS ? "card-reveal" : ""}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="font-semibold text-sm leading-tight">{d.product_name || "—"}</div>
                        {!editable && <Lock className="w-3 h-3 text-neutral-400 shrink-0" />}
                      </div>
                      <div className="flex items-center gap-1.5 flex-wrap mt-1">
                        {d.product_code && (
                          <span className="text-[10px] font-mono text-primary/80 bg-primary/5 px-1.5 py-0.5 rounded">{d.product_code}</span>
                        )}
                        {dealProducts(d).length > 1 && (
                          <span data-testid={`card-more-products-${d.id}`}
                            className="text-[10px] font-semibold bg-primary/10 text-primary px-1.5 py-0.5 rounded">
                            +{dealProducts(d).length - 1} more
                          </span>
                        )}
                        {d.stage === "Won" && dealProducts(d).some((p) => p.status === "not_taken") && (
                          <span data-testid={`card-partial-loss-${d.id}`}
                            title={`${dealProducts(d).filter((p) => p.status === "not_taken").length} product(s) lost on this deal`}
                            className="inline-flex items-center gap-0.5 text-[10px] font-semibold bg-destructive/10 text-destructive border border-destructive/30 px-1.5 py-0.5 rounded">
                            <XCircle className="w-2.5 h-2.5" />
                            {dealProducts(d).filter((p) => p.status === "not_taken").length} lost
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1.5 truncate">{d.contact_name}</div>
                      <div className="flex items-center justify-between gap-2 mt-2">
                        <div className="text-sm font-bold text-primary">
                          {d.stage === "Won" ? format(d.actual_value) : format(d.estimated_value)}
                        </div>
                        <div className="text-[10px] text-muted-foreground whitespace-nowrap" title={`Created: ${fmtDate(d.created_at)}`}>{fmtDate(d.created_at)}</div>
                        <div className="text-[10px] text-muted-foreground truncate">{d.created_by_name?.split(" ")[0]}</div>
                      </div>
                      {(() => {
                        const fu = followUpStatus(d);
                        if (!fu) return null;
                        const cls = fu === "overdue"
                          ? "bg-red-100 text-red-800 border-red-300"
                          : "bg-amber-100 text-amber-900 border-amber-300";
                        const label = fu === "overdue" ? "Overdue" : "Due today";
                        return (
                          <div className={`mt-1.5 text-[10px] font-semibold border rounded px-1.5 py-0.5 inline-block ${cls}`}
                               title={`Follow-up: ${fmtDate(d.follow_up_date)}`}>
                            {fu === "overdue" ? "⏰" : "🔔"} {label}
                          </div>
                        );
                      })()}
                      {(() => {
                        const days = stageAgeDays(d);
                        const level = agingLevel(days);
                        if (!level) return null;
                        const cls = level === "critical"
                          ? "bg-red-100 text-red-800 border-red-200"
                          : "bg-orange-100 text-orange-800 border-orange-200";
                        return (
                          <div className={`mt-1.5 text-[10px] font-semibold border rounded px-1.5 py-0.5 inline-block ${cls}`}
                               title={`This deal has been in the same stage for ${days} days`}>
                            ⚠ {days}d in stage
                          </div>
                        );
                      })()}
                      {timeLeft(d) && user.id === d.created_by && (
                        <div className="text-[10px] text-orange-700 mt-1">{timeLeft(d)}</div>
                      )}
                    </div>
                  );
                })}
                {hiddenCount(s) > 0 && (
                  <button type="button" data-testid={`show-more-${s.toLowerCase().replace(/ /g, "-")}`}
                    onClick={() => toggleColumn(s)}
                    className="w-full flex items-center justify-center gap-1.5 rounded-md border border-dashed border-border bg-secondary/40 hover:bg-secondary/70 text-xs font-semibold text-muted-foreground hover:text-neutral-800 py-2 transition-colors">
                    {expandedColumns[s] ? (
                      <><ChevronUp className="w-3.5 h-3.5" />Show less</>
                    ) : (
                      <><ChevronDown className="w-3.5 h-3.5" />
                        Show {hiddenCount(s)} more {hiddenCount(s) === 1 ? "deal" : "deals"}</>
                    )}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-md border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-secondary/60 text-[11px] uppercase tracking-widest text-muted-foreground">
              <tr>
                <SortableTh label="Product" field="product_name" sort={sort} onSort={toggleSort} />
                <th className="text-left px-4 py-3">Code</th>
                <SortableTh label="Company" field="contact_name" sort={sort} onSort={toggleSort} />
                <SortableTh label="Stage" field="stage" sort={sort} onSort={toggleSort} />
                <th className="text-right px-4 py-3">Estimated</th>
                <th className="text-right px-4 py-3">Booked (EXW)</th>
                <SortableTh label="Owner" field="created_by_name" sort={sort} onSort={toggleSort} />
                <th className="text-left px-4 py-3">Created</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedDeals.map((d) => {
                const editable = canEdit(user, d);
                return (
                  <tr key={d.id} data-testid={`deal-row-${d.id}`} className="border-t border-border hover:bg-neutral-50 cursor-pointer" onClick={() => setDetailDeal(d)}>
                    <td className="px-4 py-3 font-semibold">
                      {dealProducts(d).length > 1
                        ? dealProducts(d).map((p) => p.product_name).filter(Boolean).join(", ")
                        : (d.product_name || "—")}
                    </td>
                    <td className="px-4 py-3 text-xs font-mono text-muted-foreground">
                      {dealProducts(d).length > 1
                        ? dealProducts(d).map((p) => p.product_code).filter(Boolean).join(", ")
                        : (d.product_code || "—")}
                    </td>
                    <td className="px-4 py-3">{d.contact_name}</td>
                    <td className="px-4 py-3"><span className={`text-xs px-2 py-1 rounded border ${STAGE_COLORS[d.stage]}`}>{d.stage}</span></td>
                    <td className="px-4 py-3 text-right tabular-nums">{format(d.estimated_value)}</td>
                    <td className="px-4 py-3 text-right tabular-nums font-semibold" data-testid={`deal-booked-${d.id}`}>{d.actual_value != null ? format(d.actual_value) : "—"}</td>
                    <td className="px-4 py-3 text-xs">{d.created_by_name}</td>
                    <td className="px-4 py-3 text-xs">{fmtDate(d.created_at)}</td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        {editable ? (
                          <Button size="sm" variant="ghost" data-testid={`deal-edit-${d.id}`} onClick={() => setDialog({ open: true, initial: d })}><Pencil className="w-3.5 h-3.5" /></Button>
                        ) : <Lock className="w-3.5 h-3.5 text-neutral-400" />}
                        {user.role === "manager" && (
                          <Button size="sm" variant="ghost" data-testid={`deal-delete-${d.id}`} onClick={() => remove(d)} className="text-destructive"><Trash2 className="w-3.5 h-3.5" /></Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <DealForm open={dialog.open} onClose={() => setDialog({ open: false, initial: null })}
        initial={dialog.initial} contacts={contacts} users={users} fields={fields}
        products={products} currentUser={user} onSave={load} />
      <DealDetail deal={detailDeal} onClose={() => setDetailDeal(null)} onRefresh={async () => {
        const r = await api.get(`/deals/${detailDeal.id}`); setDetailDeal(r.data); load();
      }} user={user} onChangeStage={changeStage}
      onEditLostReason={(d) => setLostPrompt({ deal: d, edit: true })}
      onEditWonDetails={(d) => setWonPrompt({ deal: d, edit: true })} />
      <WonDetailsDialog prompt={wonPrompt} onClose={() => setWonPrompt(null)}
        onNotTaken={(d) => { setWonPrompt(null); setLostPrompt({ deal: d }); }}
        onSaved={async () => {
          await load();
          if (detailDeal?.id === wonPrompt?.deal?.id) {
            const r = await api.get(`/deals/${wonPrompt.deal.id}`); setDetailDeal(r.data);
          }
        }} />
      <LostReasonDialog prompt={lostPrompt} onClose={() => setLostPrompt(null)}
        onSaved={async () => {
          await load();
          if (detailDeal?.id === lostPrompt?.deal?.id) {
            const r = await api.get(`/deals/${lostPrompt.deal.id}`); setDetailDeal(r.data);
          }
        }} />
      <SampleSentDialog prompt={samplePrompt} onClose={() => setSamplePrompt(null)}
        onSaved={async () => {
          await load();
          if (detailDeal?.id === samplePrompt?.deal?.id) {
            const r = await api.get(`/deals/${samplePrompt.deal.id}`); setDetailDeal(r.data);
          }
        }} />
      <DuplicatesDialog resource="deals" open={dupOpen} onClose={() => setDupOpen(false)} onChanged={load} />
    </div>
  );
}
